/* ============================================================
   SHINE & STYLE — DRESSING EASYSELL

   EasySell renders the cash-on-delivery form, and it arrives in
   the colours of whatever it was set up with: a black, 5px-corner
   button in Montserrat, dropped into a white-and-red shop where
   every other button is a red pill. It also arrives in English on
   the Arabic site.

   Both are fixed here, before the app renders, by editing the
   config object it is about to read.

   WHY BEFORE, AND WHY THIS IS SAFE

   The button's styles are written as inline `!important`
   declarations built from settings.cpbStyle, and an inline
   !important cannot be beaten from a stylesheet at any
   specificity. Restyling it afterwards would mean a second pass
   of JavaScript reaching into the app's DOM after every render.
   Changing the numbers it builds those styles from is one
   assignment, and it is correct the first time it paints - no
   flash of a black button.

   The ordering this depends on is guaranteed, not lucky.
   EASYSELL_CONFIG is declared by an inline script during parsing.
   This file and the app's three modules are all deferred, so they
   run after parsing in document order, and ss-head.liquid puts
   this one first. So: config exists, app has not started.

   Verified in the page rather than assumed - see the marker at
   the bottom.

   Nothing here invents configuration. Prices, countries, shipping
   rates, which fields exist, whether the app runs at all - all of
   that stays exactly as set in EasySell's own dashboard. This
   only touches wording and colour.
   ============================================================ */
(function () {
  'use strict';

  var cfg = window.EASYSELL_CONFIG;

  /* Off, or already running. Either way, not ours to touch. */
  if (!cfg || !cfg.settings || cfg.status === 'disabled' || cfg.status === 'error') return;

  var s = cfg.settings;
  var i;

  /* Set on <html> while the config is being read - before the app
     renders and before first paint - so the stylesheet can lay out
     for a page that has a COD button on it without anything
     changing weight afterwards. */
  document.documentElement.classList.add('ss-es-live');

  /* ---- One COD button, not two ---------------------------------
     The app inserts its button at #es-form-hook AND at its own
     default targets. It only skips the defaults when its
     isPageBuilder() check passes, and a normal Shopify theme is
     not a page builder - so a hook does not replace the default
     placement, it adds to it. The cart ended up with two identical
     "Order Now - Cash on Delivery" buttons stacked in the summary.

     With form_type "popup" the default target is `beforebegin` of
     [name="checkout"], which is already inside the summary card
     and exactly where the hook was aiming. So for popup the hook
     is pure duplication and goes.

     It stays for "embedded", where the hook is the only thing
     stopping the whole form from rendering at the bottom of the
     document - there the app renders the form at the hook and
     inserts no button at all.

     Removed here rather than never rendered in Liquid because the
     setting lives in the app, not the theme: switch the form to
     embedded in EasySell's dashboard and the hook is simply left
     in place on the next load, with nothing to change here.
     ------------------------------------------------------------ */
  if (s.form_type === 'popup') {
    var hook = document.getElementById('es-form-hook');
    if (hook) hook.remove();
  }

  /* ---- The shop's colours ------------------------------------
     Literal hex rather than var(--r-500): these values are
     interpolated into an inline style attribute on an element the
     app builds, and a custom property that failed to resolve
     would leave the button transparent rather than merely wrong.
     Kept in step with ss-red.css by hand; there are two of them.
     ------------------------------------------------------------ */
  var RED = '#c0102a';        /* --r-500, 6.7:1 on white */
  var RED_DARK = '#a10d23';   /* --r-600 */

  if (s.cpbStyle) {
    s.cpbStyle.background = RED;
    s.cpbStyle.borderRadius = 999;
    s.cpbStyle.borderWidth = 0;
    s.cpbStyle.color = '#ffffff';
  }

  /* The form is a white card over a dim backdrop. It only needs
     its corners to match the rest of the shop's cards. */
  if (s.formStyle) {
    s.formStyle.borderRadius = 16;   /* --ss-radius-lg */
    s.formStyle.borderWidth = 0;
  }

  /* The submit button inside the form carries its own colours,
     separate from the button that opens it. */
  var fields = cfg.fields || [];
  for (i = 0; i < fields.length; i++) {
    if (fields[i].value === 'cta') {
      fields[i].backgroundColor = RED_DARK;
      fields[i].color = '#ffffff';
    }
  }

  /* ---- Arabic -------------------------------------------------
     The app holds one set of labels and it is English. On /ar
     that put an English form in front of half the shop's
     customers - the layout flipped to RTL because it inherits the
     page, but every word inside it was English.

     Written here rather than left to the dashboard because
     EasySell stores one set of strings per shop, not one per
     language, so there is no setting there that would fix it.

     Wording only. Field names, order and validation are
     untouched, so what reaches the order is identical.
     ------------------------------------------------------------ */
  var lang = (document.documentElement.lang || '').toLowerCase();
  if (lang.indexOf('ar') !== 0) {
    window.SS_EASYSELL_DRESSED = 'colours';
    return;
  }

  var AR = {
    header: 'يرجى تعبئة النموذج لإتمام الطلب',
    first_name: 'الاسم',
    phone: 'رقم الهاتف',
    address: 'العنوان',
    city: 'المنطقة',
    marketing: 'اشترك ليصلك كل جديد من المنتجات والعروض',
    cta: 'اطلب الآن — {total}',
    button: 'اطلب الآن — الدفع عند الاستلام',
    total: 'الإجمالي',
    subtotal: 'المجموع',
    shipping: 'التوصيل',
    discount: 'الحسم',
    free: 'مجاني',
    shipping_options: 'خيارات التوصيل',
    cart_content: 'طلبك',
    order_summary: 'ملخص الطلب',
    required: 'هذا الحقل مطلوب.',
    invalid: 'هذا الحقل غير صحيح.',
    soldout: 'نفدت الكمية'
  };

  s.cpb_text = AR.button;

  /* rtl drives the app's own mirroring - which side the icon sits
     on, how labels align - which is separate from the direction
     the form inherits from the page. */
  if (s.formConfig) {
    s.formConfig.rtl = true;
    s.formConfig.requiredMessage = AR.required;
    s.formConfig.invalidMessage = AR.invalid;
    s.formConfig.soldoutLabel = AR.soldout;
  }

  /* A text field shows `title` above it and `placeholder` inside
     it. Setting one and not the other leaves the form half
     translated, which reads worse than not translating it. */
  function text(f, word) {
    f.title = word;
    f.placeholder = word;
    f.label = word;
  }

  for (i = 0; i < fields.length; i++) {
    var f = fields[i];
    switch (f.value) {
      case 'header':     f.title = AR.header; break;
      case 'first_name': text(f, AR.first_name); break;
      case 'phone':      text(f, AR.phone); break;
      case 'address':    text(f, AR.address); break;
      case 'city':       text(f, AR.city); break;

      /* {total} is a placeholder the app substitutes. Keep it. */
      case 'cta':        f.title = AR.cta; break;

      case 'cart_content': f.label = AR.cart_content; break;

      case 'buyer_accepts_marketing':
        /* An array of one, and the app renders each entry beside
           its own checkbox. Replacing the array wholesale would
           silently drop any option added later in the dashboard,
           so only the first entry is touched. */
        if (f.options && f.options.length) f.options[0] = AR.marketing;
        break;

      case 'order_summary':
        f.label = AR.order_summary;
        if (f.labels) {
          f.labels.total = AR.total;
          f.labels.subtotal = AR.subtotal;
          f.labels.shipping = AR.shipping;
          f.labels.discount = AR.discount;
        }
        break;

      case 'shipping_options':
        f.label = AR.shipping_options;
        if (f.labels) {
          f.labels.free = AR.free;
          f.labels.shipping_options = AR.shipping_options;
        }
        break;
    }
  }

  /* Read back in the browser to confirm this ran before the app,
     rather than trusting the spec on deferred execution order. */
  window.SS_EASYSELL_DRESSED = 'colours+ar';
})();


/* ============================================================
   SHINE & STYLE — "USE MY CURRENT LOCATION"

   Lebanese addresses are landmarks, not street numbers, and a
   shopper typing one into a phone is the last place an order gets
   abandoned. This puts a button under the address field in the
   EasySell form that fills it from the phone's own location.

   What it writes:

     Batrakiye, Bachoura, Beirut
     https://maps.google.com/?q=33.893800,35.501800

   The pin matters more than the words. A courier given a pin finds
   the door; a courier given "near the pharmacy" phones the
   customer. Both go in - the words are what the customer
   recognises as their address, the link is what gets it delivered.

   It also sets the Region select, matched on ISO code - LB-BA from
   the geocoder becomes LB_BA in the form - rather than on the
   region's name, which is spelled differently by every source that
   publishes one: Bayrut, Beirut, Beyrouth.

   FAILURE IS NORMAL HERE, AND IS TREATED AS NORMAL

   Permission gets denied, GPS times out indoors, the geocoder has
   a bad day. Every one of those still leaves the shopper able to
   type, and any of them that produced coordinates still writes the
   pin, which is the useful half. Nothing here clears a field the
   shopper has already filled.

   The coordinates go to one third party, BigDataCloud, to be
   turned into words. No name, phone or order detail goes with
   them, and if that call fails the pin is still written.
   ============================================================ */
(function () {
  'use strict';

  var cfg = window.EASYSELL_CONFIG;
  if (!cfg || cfg.status === 'disabled' || cfg.status === 'error') return;
  if (!('geolocation' in navigator)) return;

  /* Browsers refuse geolocation outside a secure context, so on
     plain http the button would exist only to fail. */
  if (!window.isSecureContext) return;

  var ar = (document.documentElement.lang || '').toLowerCase().indexOf('ar') === 0;
  var JOIN = ar ? '، ' : ', ';   /* Arabic comma */

  var T = ar ? {
    idle:    'استخدم موقعي الحالي',
    busy:    'جارٍ تحديد موقعك...',
    done:    'تم تحديد موقعك',
    denied:  'الموقع مرفوض — فعّله من إعدادات المتصفح أو اكتب العنوان',
    failed:  'تعذّر تحديد الموقع — حاول مرة أخرى أو اكتب العنوان',
    pinOnly: 'أضفنا رابط موقعك — أضف اسم المبنى والطابق'
  } : {
    idle:    'Use my current location',
    busy:    'Finding you...',
    done:    'Location added',
    denied:  'Location is blocked - allow it in your browser, or type the address',
    failed:  'Could not get your location - try again, or type the address',
    pinOnly: 'Added your map pin - add the building and floor'
  };

  var PIN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s-7-5.5-7-11a7 7 0 1 1 14 0c0 5.5-7 11-7 11z"/><circle cx="12" cy="10" r="2.6"/></svg>';

  /* Vue renders these inputs, so a value assigned straight to
     .value is invisible to it until an input event tells it to
     read the DOM back. Without the event the field looks filled
     and submits empty. */
  function setValue(el, value, eventName) {
    el.value = value;
    el.dispatchEvent(new Event(eventName, { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function build(addressInput) {
    var btn = document.createElement('button');
    /* Inside a form, a button with no type IS a submit button.
       Without this line, asking for a location places the order. */
    btn.type = 'button';
    btn.className = 'ss-geo';
    btn.innerHTML = PIN + '<span></span>';

    var label = btn.querySelector('span');
    function say(text, state) {
      label.textContent = text;
      btn.className = 'ss-geo' + (state ? ' is-' + state : '');
      btn.disabled = state === 'busy' || state === 'done';
    }
    say(T.idle, '');

    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      say(T.busy, 'busy');

      navigator.geolocation.getCurrentPosition(function (pos) {
        var lat = pos.coords.latitude;
        var lon = pos.coords.longitude;
        var pin = 'https://maps.google.com/?q=' + lat.toFixed(6) + ',' + lon.toFixed(6);

        /* Names first, pin second - and the pin alone if the
           lookup fails, because coordinates are already enough to
           deliver to. */
        /* Separated with a dash, not a newline. The address is an
           <input type="text">, and an input strips newlines on
           assignment - which glued the last word straight onto the
           URL: "Bayruthttps://maps.google.com/...". */
        function write(words) {
          setValue(addressInput, words ? words + ' - ' + pin : pin, 'input');
          say(words ? T.done : T.pinOnly, 'done');
        }

        fetch('https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=' +
              lat + '&longitude=' + lon + '&localityLanguage=' + (ar ? 'ar' : 'en'))
          .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
          .then(function (geo) {
            /* localityInfo.administrative runs widest to narrowest
               - country, governorate, district, neighbourhood.
               Reversed and trimmed to three it reads the way a
               person says an address, and the country is dropped
               because the shop ships to one. */
            var parts = [];
            var admin = (geo.localityInfo && geo.localityInfo.administrative) || [];
            for (var i = admin.length - 1; i >= 0 && parts.length < 3; i--) {
              var name = admin[i] && admin[i].name;
              if (name && name !== geo.countryName && parts.indexOf(name) === -1) parts.push(name);
            }
            if (!parts.length && geo.locality) parts.push(geo.locality);

            /* LB-BA from the geocoder, LB_BA in the form. Set only
               where that exact option exists, so an unknown or
               foreign code leaves the shopper's own choice alone. */
            var select = document.querySelector('#easysell select[name="city"]');
            var code = (geo.principalSubdivisionCode || '').replace('-', '_');
            if (select && code) {
              for (var j = 0; j < select.options.length; j++) {
                if (select.options[j].value === code) {
                  setValue(select, code, 'change');
                  break;
                }
              }
            }

            write(parts.join(JOIN));
          })
          .catch(function () { write(null); });

      }, function (err) {
        /* code 1 is PERMISSION_DENIED, and it is the only one the
           shopper can do anything about, so it gets its own
           sentence. */
        say(err && err.code === 1 ? T.denied : T.failed, 'error');
      }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 });
    });

    return btn;
  }

  /* The form is built when the popup first opens and rebuilt when
     the cart changes, so this watches rather than running once.
     The guard is a lookup on the field's own container, so a
     rebuilt form gets a new button and a re-rendered one does not
     get two. */
  function attach() {
    var input = document.querySelector('#easysell input[name="address"]');
    if (!input) return;
    var column = input.closest('.es-input-column') || input.parentElement;
    if (!column || column.querySelector('.ss-geo')) return;
    column.appendChild(build(input));
  }

  if (document.readyState !== 'loading') attach();
  else document.addEventListener('DOMContentLoaded', attach);

  if ('MutationObserver' in window) {
    new MutationObserver(attach).observe(document.body, { childList: true, subtree: true });
  }
})();
